import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import utilsService from "../api/utilsServices";

const emptyNormalized = {
  industries: [],
  menuByIndustry: {},
  categoryOptions: [],
};

const CategoriesContext = createContext(null);

export function CategoriesProvider({ children }) {
  const [snapshot, setSnapshot] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await utilsService.fetchCategoriesNormalized({ force: false });
        if (!cancelled && res?.status) setSnapshot(res);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const refetch = useCallback(async () => {
    const res = await utilsService.fetchCategoriesNormalized({ force: true });
    if (res?.status) setSnapshot(res);
  }, []);

  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void utilsService.fetchCategoriesNormalized({ force: false }).then((res) => {
        if (res?.status) setSnapshot(res);
      });
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const value = useMemo(() => {
    const normalized = snapshot?.normalized ?? emptyNormalized;
    return {
      loading,
      normalized,
      industries: normalized.industries,
      menuByIndustry: normalized.menuByIndustry,
      categoryOptions: normalized.categoryOptions,
      signupCategories: snapshot?.signupCategories ?? [],
      pointInTime: snapshot?.pointInTime ?? null,
      refetch,
    };
  }, [loading, snapshot, refetch]);

  return (
    <CategoriesContext.Provider value={value}>{children}</CategoriesContext.Provider>
  );
}

export function useCategories() {
  const ctx = useContext(CategoriesContext);
  if (!ctx) {
    throw new Error("useCategories must be used within a CategoriesProvider");
  }
  return ctx;
}
